import { useMemo, useState } from 'react';
import { DownloadIcon, PackageCheckIcon } from 'lucide-react';
import { EmptyState } from '../ui/EmptyState';
import { Stepper } from '../ui/Stepper';
import { CopyButton } from '../marketing/CopyButton';
import { btnGhost, listBox } from '../ui/styles';
import { money } from '../../core/format/currency';
import { today } from '../../core/format/dates';
import { toCsv } from '../../core/rules/csv';
import { REORDER_COVER_DAYS, clampOrderQty, orderCost, orderText, reorderLines } from '../../core/rules/reorder';
import { downloadCsv } from '../../utils/download';
import type { StockControl } from './useStock';

/** Hoger kiezen dan dit per regel kan niet; meer is vrijwel altijd een tikfout. */
const MAX_ORDER = 999;

/**
 * Tabblad Bestellijst: wat er bij de leverancier besteld moet worden, per merk. Het voorstel komt
 * uit de verkoop van de laatste 30 dagen; Sarah past de aantallen aan en kopieert de lijst in een
 * mail, of downloadt hem. Niets wordt geboekt: wat binnenkomt boek je bij Inkoop.
 */
export function ReorderList({
  stock
}: {
  stock: StockControl;
}) {
  const lines = useMemo(() => reorderLines(stock.items ?? []), [stock.items]);
  // Alleen wat Sarah aanpaste; de rest volgt het voorstel (ook na een nieuwe stand).
  const [picked, setPicked] = useState<Record<string, number>>({});
  const qtyOf = (id: string, suggested: number) => picked[id] ?? suggested;
  const chosen = lines.map(l => ({
    ...l,
    qty: qtyOf(l.itemId, l.suggested)
  }));
  const toOrder = chosen.filter(l => l.qty > 0);
  const cost = orderCost(toOrder);
  const suppliers = [...new Set(chosen.map(l => l.supplier))];
  if (lines.length === 0) {
    return <EmptyState icon={PackageCheckIcon} title="Niets te bestellen" text={`Alles wat verkocht wordt, ligt er voor minstens ${REORDER_COVER_DAYS / 7} weken.`} data-id="element-4914" data-mp="2u0t3ak89ixzv" />;
  }
  const download = () => downloadCsv(`bestellijst-${today()}.csv`, toCsv(['Leverancier', 'Artikel', 'Aantal', 'Op voorraad', 'Inkoopprijs per stuk excl. btw'], toOrder.map(l => [l.supplier, l.name, l.qty, l.onHand, l.costPriceCents === undefined ? null : l.costPriceCents / 100])));
  return <div data-id="element-4915" data-mp="1ohbwnkdbrj3l">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-4916" data-mp="1telkivl2fmpb">
        <p className="text-[13px] text-muted" data-id="element-4917" data-mp="1tgogdw8wga1y">
          Genoeg voor {REORDER_COVER_DAYS / 7} weken verkoop, gerekend op de laatste 30 dagen. Webshop en salon samen.
        </p>
        <div className="flex items-center gap-1" data-id="element-4918" data-mp="23c9peecdyal7">
          <CopyButton text={orderText(toOrder)} label="Kopieer voor mail" data-id="element-4919" data-mp="27e4odc6ugbvt" />
          <button type="button" onClick={download} disabled={toOrder.length === 0} className={btnGhost} data-id="element-4920" data-mp="27c0dubefwemk">
            <DownloadIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-4921" data-mp="2h46esamo66dw" />
            CSV
          </button>
        </div>
      </div>

      <div className="space-y-6" data-id="element-4922" data-mp="2m4c3bhxn7crr">
        {suppliers.map((supplier, i) => <section key={supplier} aria-labelledby={`bestel-leverancier-${i}`} data-id="element-4923" data-mp="2r1u7yw4mn5ty" data-mpi={i}>
            <h3 id={`bestel-leverancier-${i}`} className="mb-2 text-[14px] font-medium text-ink" data-id="element-4924" data-mp="feqstubdit6w">
              {supplier}
            </h3>
            <ul className={listBox} data-id="element-4925" data-mp="af4f7jrmvaxk">
              {chosen.filter(l => l.supplier === supplier).map(l => <li key={l.itemId} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 sm:px-5" data-id="element-4926" data-mp="hrdtle6zy2e">
                    <span className="min-w-0 flex-1" data-id="element-4927" data-mp="y58i1owzz6ni">
                      <span className="block truncate text-[14px] text-ink" data-id="element-4928" data-mp="y4qcu2scxi3g">{l.name}</span>
                      <span className="block text-[12px] text-muted" data-id="element-4929" data-mp="t6x7fbyvos48">
                        {l.onHand} op voorraad
                        {l.daysLeft !== null && <> · {l.daysLeft === 0 ? 'nu op' : `op over ± ${l.daysLeft} ${l.daysLeft === 1 ? 'dag' : 'dagen'}`}</>}
                        {l.daysLeft === null && ' · niet verkocht, wel op'}
                        {l.qty !== l.suggested && <> · voorstel {l.suggested}</>}
                      </span>
                    </span>
                    <span className="w-20 text-right text-[13px] tabular-nums text-muted" data-id="element-4930" data-mp="wm878snpyhfn">
                      {l.costPriceCents === undefined ? '—' : money(l.costPriceCents * l.qty)}
                    </span>
                    <Stepper label={`Aantal ${l.name}`} value={l.qty} min={0} max={MAX_ORDER} onChange={qty => setPicked(p => ({
              ...p,
              [l.itemId]: clampOrderQty(qty)
            }))} data-id="element-4931" data-mp="mqn1qkjfyz15" />
                  </li>)}
            </ul>
          </section>)}
      </div>

      <p className="mt-5 text-[13px] text-muted" data-id="element-4932" data-mp="15nerncthoce6">
        {toOrder.length} {toOrder.length === 1 ? 'regel' : 'regels'} · inkoopwaarde <span className="tabular-nums text-ink" data-id="element-4933" data-mp="1akodyjll07xs">{money(cost.cents)}</span> excl. btw
        {cost.unknown > 0 && <span className="text-clay" data-id="element-4934" data-mp="1yasl30xzaptj"> · {cost.unknown} zonder inkoopprijs telt niet mee</span>}
      </p>
      <p className="mt-2 text-[12px] leading-relaxed text-muted" data-id="element-4935" data-mp="3ntr2o0a17yzh">Deze lijst boekt niets. Komt de levering binnen, boek hem dan bij Inkoop vanaf de factuur.</p>
    </div>;
}